import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";

import {
  getLoginId,
  getUserIdentityService,
  getUserIdentityServiceFromDetailsApi,
} from "../../../api/authService";

import {
  getSetupListInfo,
  getTranslatedDropdownLabel,
  getUserPermissions,
} from "../../../pages/LandingPage/helpers";

import { getSetupListNavInfoByDepartment } from "../../../pages/LandingPage/helpers";

import {
  DEFAULT_DEPARTMENT_OPTIONS,
  FEATURE_FLAG_KEYS,
  TOAST_MESSAGES,
} from "../../../pages/LandingPage/constants";

import {
  DevUserList,
  IAMUserState,
  SetupListNavInfoByDepartment,
  UserData,
  GeneralPermissions,
  SetupPermission,
  LandingPageOptions,
  NavItem,
  IOption,
  ToastInfo,
} from "../../../types";

import { SetupBaseRequestInfo } from "../../../types/types.api";
import {
  EffectiveUserContext,
  resolveLaunchContext,
} from "../launch/resolveLaunchContext";

interface UseUserInitializationParams {
  user: UserData;

  setUser: React.Dispatch<React.SetStateAction<UserData>>;

  isDevUser: boolean;
  setIsDevUser: React.Dispatch<React.SetStateAction<boolean>>;

  setPermissions: React.Dispatch<
    React.SetStateAction<{
      userPermission: GeneralPermissions;
      setupPermission: SetupPermission;
    }>
  >;

  selected: LandingPageOptions;

  setSelected: React.Dispatch<React.SetStateAction<LandingPageOptions>>;

  setDefaultStore: React.Dispatch<React.SetStateAction<IOption>>;

  setIamUserState: React.Dispatch<React.SetStateAction<IAMUserState>>;

  setIsFetchingUserData: React.Dispatch<React.SetStateAction<boolean>>;

  setSetupListNavInfoByDepartment: React.Dispatch<
    React.SetStateAction<SetupListNavInfoByDepartment>
  >;

  setFavorites: React.Dispatch<React.SetStateAction<NavItem[]>>;

  setInitialFavorites: React.Dispatch<React.SetStateAction<NavItem[]>>;

  setNetworkErrorToast: (toast: ToastInfo) => void;

  setResolvedLaunchContext: React.Dispatch<
    React.SetStateAction<EffectiveUserContext>
  >;
}

export const useUserInitialization = ({
  setIsDevUser,
  setUser,
  setPermissions,
  selected,
  setSelected,
  setDefaultStore,
  setIamUserState,
  setIsFetchingUserData,
  setSetupListNavInfoByDepartment,
  setFavorites,
  setInitialFavorites,
  setNetworkErrorToast,
  setResolvedLaunchContext,
}: UseUserInitializationParams) => {
  const { t } = useTranslation();

  const hasInitialized = useRef(false);

  const enableRbacSetupManagement = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_RBAC_SETUP_MANAGEMENT,
    "false",
  );

  const devUsersList = useFeatureFlag(
    FEATURE_FLAG_KEYS.DEV_USER_ACCESS_LIST_KEY,
    { users: [] },
  );

  const devUsersData: DevUserList =
    typeof devUsersList === "string" ? JSON.parse(devUsersList) : devUsersList;

  const devUsersDataRef = useRef<DevUserList>(devUsersData);

  useEffect(() => {
    devUsersDataRef.current = devUsersData;
  }, [devUsersData]);

  const isDevUserLoginId = useCallback((loginId?: string): boolean => {
    const { users } = devUsersDataRef.current;

    return Boolean(
      loginId &&
      Array.isArray(users) &&
      users.some(
        (devUserLoginId) =>
          devUserLoginId.toLowerCase() === loginId.toLowerCase(),
      ),
    );
  }, []);

  const initializeUserData = useCallback(async (): Promise<void> => {
    try {
      const [
        userIdentityServiceResult,
        userIdentityServiceFromDetailsApiResult,
        userPermissionsResult,
      ] = await Promise.all([
        getUserIdentityService(),
        getUserIdentityServiceFromDetailsApi(),
        getUserPermissions(getLoginId()),
      ]);
      const { user, enterprise, stores } = userIdentityServiceResult;
      const iamUserState = userIdentityServiceFromDetailsApiResult;
      const { permissionsList, userPermissions } = userPermissionsResult;

      setUser(userIdentityServiceResult);
      const isDevUser = isDevUserLoginId(user.loginId);
      setIsDevUser(isDevUser);
      setPermissions((previous) => ({
        ...previous,
        userPermission: userPermissions,
      }));

      /**
       * Since we are not supporting for internal users,
       * we may ignore effectiveIamUserState, effectiveIamUserStoresCommaSeparated.
       * We will still retrieve them from the launch context, but they won't affect the current user initialization flow.
       */
      const {
        launchMode,
        effectiveEnterpriseId,
        effectiveStoreOption,
        effectiveIamUserState,
        effectiveIamUserStoresCommaSeparated,
      } = await resolveLaunchContext({
        enterpriseId: enterprise.id,
        stores,
        iamUserState,
      });
      setResolvedLaunchContext({
        launchMode,
        effectiveEnterpriseId,
        effectiveStoreOption,
        effectiveIamUserState,
        effectiveIamUserStoresCommaSeparated,
      });

      if (!effectiveIamUserState?.eisGroups?.groups?.length) {
        setNetworkErrorToast(TOAST_MESSAGES.USER_DATA_FAILURE());
        return;
      }

      setIamUserState(effectiveIamUserState);
      setSelected((previous) => ({
        ...previous,
        store: effectiveStoreOption,
      }));

      setDefaultStore(effectiveStoreOption);

      const setupBaseRequestInfo: SetupBaseRequestInfo = {
        storeId: effectiveStoreOption.value,
        enterpriseId: effectiveEnterpriseId,
        functionalArea: selected.department.value,
        functionalUnitName: selected.setup.value,
        departmentId: "",
        accountName: "",
        stores: effectiveIamUserStoresCommaSeparated,
      };

      setIsFetchingUserData(true);

      const { setupListInfo, favoritesList, setupListToastInfo } =
        await getSetupListInfo({
          setupBaseRequestInfo,
        });

      if (setupListToastInfo) {
        setNetworkErrorToast(setupListToastInfo);
        return;
      }

      const { setupListNavInfoByDepartment, favoritesSetupInfoList } =
        getSetupListNavInfoByDepartment({
          setupListInfo,
          favoritesList,
          departments: DEFAULT_DEPARTMENT_OPTIONS,
          permissionsList,
          enableRbacSetupManagement,
          isDevUser,
        });

      setFavorites(favoritesSetupInfoList);
      setInitialFavorites(favoritesSetupInfoList);
      setSetupListNavInfoByDepartment(setupListNavInfoByDepartment);
    } catch (error) {
      console.error("Unable to initializeUserData", error);

      setNetworkErrorToast(TOAST_MESSAGES.USER_DATA_FAILURE());
    }
  }, [
    enableRbacSetupManagement,
    isDevUserLoginId,
    selected.department.value,
    selected.setup.value,
    setDefaultStore,
    setFavorites,
    setInitialFavorites,
    setIsFetchingUserData,
    setNetworkErrorToast,
    setSelected,
    setSetupListNavInfoByDepartment,
    setUser,
  ]);

  /**
   * Translates the labels for the department and setup dropdowns based on the current language.
   * @remarks
   * This effect will automatically update the labels of the department and setup dropdowns whenever the translation function `t` changes.
   */
  useEffect(() => {
    setSelected((previous) => {
      if (!previous?.department?.value || !previous?.setup?.value) {
        return previous;
      }

      return {
        ...previous,

        department: {
          ...previous.department,
          label: getTranslatedDropdownLabel(
            "application",
            previous.department.value,
            previous.department.label,
            t,
          ),
        },

        setup: {
          ...previous.setup,
          label: getTranslatedDropdownLabel(
            "setup",
            previous.setup.value,
            previous.setup.label,
            t,
          ),
        },
      };
    });
  }, [t, setSelected]);

  /**
   * Initializes the user data when the component is mounted.
   * @remarks
   * This effect will only run once and will fetch the necessary user data, setting the appropriate state values.
   */
  useEffect(() => {
    const initialize = async (): Promise<void> => {
      if (window.location.pathname === "/logout-success") {
        return;
      }

      if (hasInitialized.current) {
        return;
      }

      hasInitialized.current = true;

      setIsFetchingUserData(true);

      await initializeUserData();

      setIsFetchingUserData(false);
    };

    void initialize();
  }, [initializeUserData, setIsFetchingUserData]);
};
